/**
 * Permate Tracking Utilities
 * Các function để xử lý tracking với Permate
 */

import { permateConfig } from '@/config/permate.config';

// Types
export interface PermateParams {
  pm_offer_id?: string;
  click_id?: string;
  pm_click_id?: string; // Permate sử dụng pm_click_id
  pm_cid?: string; // Campaign ID từ Permate
  sm_cid?: string;
  pm_campaign_id?: string;
  pm?: string;
  pm_test?: string;
  pm_is_test_redirect_302?: string;
  event_id?: string;
  utm_content?: string; // Event ID từ URL
  utm_term?: string; // Offer ID từ URL
}

export interface ConversionData {
  external_id: string;
  sale_value: number;
  external_conversion_id?: string;
  conversion_type?: string;
  custom_data?: Record<string, any>;
}

/**
 * Lấy Permate tracking parameters từ URL
 */
export function getPermateParams(): PermateParams {
  if (typeof window === 'undefined') return {};

  const urlParams = new URLSearchParams(window.location.search);
  const sessionParams = getPermateParamsFromSession();

  const params: PermateParams = {
    pm_offer_id: urlParams.get('pm_offer_id') ?? sessionParams.pm_offer_id,
    click_id: urlParams.get('click_id') ?? sessionParams.click_id,
    pm_click_id: urlParams.get('pm_click_id') ?? sessionParams.pm_click_id,
    pm_cid: urlParams.get('pm_cid') ?? sessionParams.pm_cid,
    sm_cid: urlParams.get('sm_cid') ?? sessionParams.sm_cid,
    pm_campaign_id:
      urlParams.get('pm_campaign_id') ?? sessionParams.pm_campaign_id,
    pm: urlParams.get('pm') ?? sessionParams.pm,
    pm_test: urlParams.get('pm_test') ?? sessionParams.pm_test,
    pm_is_test_redirect_302:
      urlParams.get('pm_is_test_redirect_302') ??
      sessionParams.pm_is_test_redirect_302,
    event_id: urlParams.get('event_id') ?? sessionParams.event_id,
    utm_content: urlParams.get('utm_content') ?? sessionParams.utm_content,
    utm_term: urlParams.get('utm_term') ?? sessionParams.utm_term,
  };

  // Lưu params vào session nếu có
  if (Object.values(params).some((v) => v)) {
    savePermateParamsToSession(params);
  }

  return params;
}

/**
 * Lưu Permate params vào sessionStorage
 */
export function savePermateParamsToSession(params: PermateParams): void {
  if (typeof window === 'undefined') return;

  try {
    sessionStorage.setItem('permate_params', JSON.stringify(params));
  } catch (error) {
    console.warn('Failed to save Permate params to session:', error);
  }
}

/**
 * Lấy Permate params từ sessionStorage
 */
export function getPermateParamsFromSession(): PermateParams {
  if (typeof window === 'undefined') return {};

  try {
    const stored = sessionStorage.getItem('permate_params');
    return stored ? JSON.parse(stored) : {};
  } catch (error) {
    console.warn('Failed to get Permate params from session:', error);
    return {};
  }
}

/**
 * Gửi conversion tracking đến Permate (Server-side Postback)
 */
export async function sendPermateConversion(
  conversionData: ConversionData
): Promise<boolean> {
  try {
    const permateParams = getPermateParams();

    // Get click_id from either old or new parameter format
    const clickId = permateParams.click_id ?? permateParams.pm_click_id;

    if (!clickId || !permateParams.pm_offer_id) {
      console.warn('Missing Permate tracking parameters for conversion');
      return false;
    }

    // Construct postback URL with required parameters
    const postbackUrl = new URL(permateConfig.postbackUrl);
    postbackUrl.searchParams.set('api_key', permateConfig.apiKey);
    postbackUrl.searchParams.set('pm_adv_id', permateConfig.advId);
    postbackUrl.searchParams.set('click_uuid', clickId); // Use click_id as click_uuid
    postbackUrl.searchParams.set('offer_id', permateConfig.offerId);

    // Add event_id from utm_content if available
    if (permateParams.utm_content) {
      postbackUrl.searchParams.set('event_id', permateParams.utm_content);
    }

    // // Add utm_content and utm_term from params
    // if (permateParams.utm_content) {
    //   postbackUrl.searchParams.set('utm_content', permateParams.utm_content);
    // }
    // if (permateParams.utm_term) {
    //   postbackUrl.searchParams.set('utm_term', permateParams.utm_term);
    // }

    // Optional parameters
    if (conversionData.external_id) {
      postbackUrl.searchParams.set('external_id', conversionData.external_id);
    }
    if (conversionData.sale_value) {
      postbackUrl.searchParams.set(
        'sale_value',
        conversionData.sale_value.toString()
      );
    }

    // Gửi postback trực tiếp đến pmcloud1.com
    const response = await fetch(postbackUrl.toString(), {
      method: 'GET', // Permate uses GET for postback
      headers: {
        'User-Agent': 'AhaFood-Postback/1.0',
      },
    });

    if (response.ok) {
      console.log(
        'Permate conversion sent successfully to:',
        postbackUrl.toString()
      );
      return true;
    } else {
      console.error('Failed to send Permate conversion:', response.statusText);
      return false;
    }
  } catch (error) {
    console.error('Error sending Permate conversion:', error);
    return false;
  }
}

/**
 * Fire Permate Pixel (Client-side tracking)
 */
export function firePermatePixel(conversionData: ConversionData): void {
  const permateParams = getPermateParams();

  // Get click_id from either old or new parameter format
  const clickId = permateParams.click_id ?? permateParams.pm_click_id;

  if (!clickId || !permateParams.pm_offer_id) {
    console.warn('Missing Permate tracking parameters for pixel');
    return;
  }

  try {
    // Construct pixel URL with same format as postback
    const pixelUrl = new URL(permateConfig.pixelUrl);
    pixelUrl.searchParams.set('api_key', permateConfig.apiKey);
    pixelUrl.searchParams.set('pm_adv_id', permateConfig.advId);
    pixelUrl.searchParams.set('click_uuid', clickId); // Use click_id as click_uuid
    pixelUrl.searchParams.set('offer_id', permateConfig.offerId);

    // Add event_id from utm_content if available
    if (permateParams.utm_content) {
      pixelUrl.searchParams.set('event_id', permateParams.utm_content);
    }

    // Add utm_content and utm_term from params
    if (permateParams.utm_content) {
      pixelUrl.searchParams.set('utm_content', permateParams.utm_content);
    }
    if (permateParams.utm_term) {
      pixelUrl.searchParams.set('utm_term', permateParams.utm_term);
    }

    // Optional parameters
    if (conversionData.external_id) {
      pixelUrl.searchParams.set('external_id', conversionData.external_id);
    }
    if (conversionData.sale_value) {
      pixelUrl.searchParams.set(
        'sale_value',
        conversionData.sale_value.toString()
      );
    }

    // Tạo và fire pixel image
    const pixelImg = new Image();
    pixelImg.onload = () => {
      console.log('Permate pixel fired successfully');
    };
    pixelImg.onerror = () => {
      console.warn('Permate pixel failed to load');
    };

    pixelImg.src = pixelUrl.toString();

    console.log('🔥 Firing Permate pixel:', pixelUrl.toString());
  } catch (error) {
    console.error('Error firing Permate pixel:', error);
  }
}

/**
 * Track conversion với cả Postback và Pixel
 */
export async function trackPermateConversion(
  conversionData: ConversionData
): Promise<void> {
  console.log('Tracking Permate conversion:', conversionData);

  // Gửi server-side postback
  await sendPermateConversion(conversionData);

  // Fire client-side pixel
  firePermatePixel(conversionData);
}

/**
 * Kiểm tra xem có traffic từ Permate không
 */
export function isPermateTraffic(): boolean {
  const params = getPermateParams();
  // Check for Permate parameters - either old format (click_id) or new format (pm_click_id)
  return !!(params.pm_offer_id && (params.click_id ?? params.pm_click_id));
}

/**
 * Clear Permate tracking data
 */
export function clearPermateParams(): void {
  if (typeof window === 'undefined') return;

  try {
    sessionStorage.removeItem('permate_params');
  } catch (error) {
    console.warn('Failed to clear Permate params:', error);
  }
}
